import { useContext, useState, forwardRef, useMemo } from 'react';
import { UnitsContext } from '../../../../Contexts/UnitsContext';
import {
	SPLIT_CONFIG_TYPES,
	AB_SPLIT_TEST_CONSTANTS,
	SPLIT_LEVELS
} from '../../../../core/constants';
import useCommonStyles from '../../../../commonStyles';
import { Modalv2Context } from '../../../../Contexts/Modalv2Context';
import VideoSectionSplitCard from './VideoSectionSplitCard';
import SwitchInput from '../../../../Components/SwitchInput';
import { checkIfNewSplitCanBeAdded } from '../../../../utilities/sectionRefHelpers';
import { toast } from 'react-toastify';
import DropDownSingle from '../../../../Components/DropDownSingle';
import PageHeading from '../../../../Components/PageHeading';
//mui imports====
import { Grid, Paper, Button, Box } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import Typography from '@mui/material/Typography';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import { FEATURE_DESCRIPTION } from '../../constant';
import InputConfig from '../../../../Components/InputConfig';

const { EXPERIMENT_SPLIT, NORMAL_SPLIT } = SPLIT_CONFIG_TYPES;
const { SECTION_LEVEL_SPLIT, SITE_LEVEL_SPLIT } = SPLIT_LEVELS;
const {
	AB_SPLIT_TYPE: { CONTROL }
} = AB_SPLIT_TEST_CONSTANTS;

/**
 * This function
 */
const VideoSectionsSplitConfig = () => {
	const { SITE_CONFIG } = FEATURE_DESCRIPTION;
	const commonStyleClasses = useCommonStyles();
	const { units, sectionLevelSplit, updateSectionLevelSplit } = useContext(UnitsContext);
	const { updateModalContent } = useContext(Modalv2Context);

	const splitLevelList = [
		{ label: 'Section Level Split', value: SECTION_LEVEL_SPLIT },
		{ label: 'Site Level Split', value: SITE_LEVEL_SPLIT }
	];

	/**
	 * This function deletes a section split from the sectionlevelsplit config
	 * @param {string} sectionId
	 */
	const deleteSectionFromSplitConfig = (sectionId) => {
		updateSectionLevelSplit((sectionLevelSplit) => {
			const newSplitConfig = structuredClone(sectionLevelSplit);
			delete newSplitConfig.splits[sectionId];
			return newSplitConfig;
		});
	};

	/**
	 * @name updateExperimentTypeSplitPercentage
	 * @description Function that updates splitPercentages of AB Split Test Sections.
	 * @param {string} sectionRef - The sectionRef of the section
	 * @param {number} newPercentage - The new percentage value
	 */
	const updateExperimentTypeSplitPercentage = (sectionRef, newPercentage) => {
		const newSplitConfig = structuredClone(sectionLevelSplit);
		const videoSectionId = units[sectionRef].videoSectionId;
		const videoSectionSplit = newSplitConfig.splits[videoSectionId];
		// Throw error if videoSectionSplit is not found
		if (!videoSectionSplit) {
			return toast.error('Section Id Not Found in Splits!', {
				position: toast.POSITION.BOTTOM_RIGHT
			});
		}
		videoSectionSplit.controlExperimentSplits[sectionRef].splitPercentage = newPercentage;
		updateSectionLevelSplit(newSplitConfig);
	};

	/**
	 * @name updateNormalTypeSplitPercentage
	 * @description This function updates the split percentage of a section split
	 * @param {string} sectionId
	 * @param {number} newPercentage
	 * @returns
	 */
	const updateNormalTypeSplitPercentage = (sectionId, newPercentage) => {
		const newSplitConfig = structuredClone(sectionLevelSplit);
		const videoSectionSplit = newSplitConfig.splits[sectionId];
		// Throw error if videoSectionSplit is not found
		if (!videoSectionSplit) {
			return toast.error('Section Id Not Found in Splits!', {
				position: toast.POSITION.BOTTOM_RIGHT
			});
		}

		videoSectionSplit.splitPercentage = newPercentage;
		updateSectionLevelSplit(newSplitConfig);
	};

	/**
	 * This function updates the enabled value of sectionLevelSplit
	 * @param {boolean} booleanValue
	 */
	const handleSectionLevelSplitEnabled = (booleanValue) => {
		const newSplitConfig = structuredClone(sectionLevelSplit);
		newSplitConfig.enabled = booleanValue;
		updateSectionLevelSplit(newSplitConfig);
	};

	/**
	 * This function updates the splitLevel value of sectionLevelSplit
	 * @param {boolean} booleanValue
	 */
	const handleSectionSplitLevel = (selectedValue) => {
		const newSplitConfig = structuredClone(sectionLevelSplit);
		newSplitConfig.splitLevel = selectedValue;
		updateSectionLevelSplit(newSplitConfig);
	};

	const isValidSplitLevelDropDown = useMemo(() => {
		const clonedSectionLevelSplit = structuredClone(sectionLevelSplit);
		const splitSections = Object.keys(clonedSectionLevelSplit.splits);
		return splitSections.length > 0;
	}, [sectionLevelSplit.splits]);

	return (
		<Paper className={commonStyleClasses.mainPagePaperMarginStyle}>
			<PageHeading
				headingName="Split Config Settings"
				headingPath={['Dashboard', 'Framerate', 'Split Config Settings']}
				description="This page allows you to configure the split settings for the video sections."
				showInfoButton={true}
				infoMessage={SITE_CONFIG.SPLIT_CONFIG_SETTINGS}
			/>
			<Box
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					padding: '6rem 5rem 1.4rem 5rem'
				}}
			>
				<SwitchInput
					isObjectEditor={false}
					label={'Enabled'}
					directValue={sectionLevelSplit.enabled}
					updateDirectValue={() => handleSectionLevelSplitEnabled(!sectionLevelSplit.enabled)}
					containerStyles={{ width: 'fit-content', margin: 0 }}
				/>

				<Grid item xs={6} style={{ paddingLeft: '3rem' }}>
					<DropDownSingle
						directValue={sectionLevelSplit.splitLevel}
						isObjectEditor={false}
						label={'Split Level'}
						formControlVariant="outlined"
						disabled={isValidSplitLevelDropDown}
						list={splitLevelList}
						selectInputStyles={{ width: '20rem' }}
						formControlStyles={{
							display: 'flex',
							flexDirection: 'row',
							alignItems: 'center',
							gap: '1rem'
						}}
						labelStyles={{
							margin: 0,
							whiteSpace: 'nowrap',
							width: '12rem'
						}}
						updateDirectValue={(value) => {
							handleSectionSplitLevel(value);
						}}
					/>
				</Grid>
			</Box>

			<Grid
				className={commonStyleClasses.basicOutlinedBox}
				container
				spacing={0}
				style={{ width: '94%', margin: '3rem auto 3rem auto' }}
			>
				{Object.keys(sectionLevelSplit.splits).map((videoSectionId) => {
					return (
						<Grid item xs={6} key={videoSectionId}>
							<VideoSectionSplitCard
								units={units}
								videoSectionId={videoSectionId}
								sectionSplitData={sectionLevelSplit.splits[videoSectionId]}
								updateNormalTypeSplitPercentage={updateNormalTypeSplitPercentage}
								updateExperimentTypeSplitPercentage={updateExperimentTypeSplitPercentage}
								deleteSectionFromSplitConfig={deleteSectionFromSplitConfig}
							/>
						</Grid>
					);
				})}
				<Grid item xs={12} style={{ display: 'flex', marginTop: '1rem' }}>
					{Object.keys(sectionLevelSplit.splits).length < Object.keys(units).length &&
						sectionLevelSplit.splitLevel === SPLIT_LEVELS.SITE_LEVEL_SPLIT && (
							<AddCircleOutlineOutlinedIcon
								className={commonStyleClasses.icon}
								color="primary"
								fontSize="medium"
								style={{ margin: '0 auto' }}
								onClick={() => {
									updateModalContent(<AddSectionSplit />);
								}}
							/>
						)}
				</Grid>
			</Grid>
		</Paper>
	);
};

const AddSectionSplit = forwardRef(() => {
	const { units, sectionLevelSplit, updateSectionLevelSplit } = useContext(UnitsContext);
	const [selectedExistingUnitSectionRef, setSelectedExistingUnitSectionRef] = useState('');
	const [selectedSectionSplitType, setSelectedSectionSplitType] = useState('');
	const [splitDescription, setSplitDescription] = useState('');
	const { updateModalContent } = useContext(Modalv2Context);

	const commonStyleClasses = useCommonStyles();
	const availableSections = useMemo(() => {
		const alreadyAddedSections = new Set(
			sectionLevelSplit ? Object.keys(sectionLevelSplit.splits) : []
		);

		const availableSectionRefs = [];
		// Iterates and adds the sectionRefs of sections that are available for split formation.
		for (const sectionRef in units) {
			const abSplitTestData = units[sectionRef]?.abSplitTestData;
			const videoSectionId = units[sectionRef].videoSectionId;
			const considerSectionRefCondition =
				!alreadyAddedSections.has(videoSectionId) &&
				!units[sectionRef].isArchived &&
				(abSplitTestData?.splitType === CONTROL || !abSplitTestData);

			if (considerSectionRefCondition) {
				availableSectionRefs.push(sectionRef);
			}
		}
		return availableSectionRefs;
	}, [sectionLevelSplit, units]);

	/**
	 * @name modifySplitConfigToAddNewSplit
	 * This function modifies the section Split config to add a new split.
	 * @returns {object} clonedSectionLevelSplit - The Updated Split Config
	 */
	const modifySplitConfigToAddNewSplit = () => {
		const clonedSectionLevelSplit = structuredClone(sectionLevelSplit);
		const videoSectionId = units[selectedExistingUnitSectionRef].videoSectionId;
		if (selectedSectionSplitType === EXPERIMENT_SPLIT) {
			const counterpartSectionRef =
				units[selectedExistingUnitSectionRef].abSplitTestData.counterpartSectionRef;
			clonedSectionLevelSplit.splits[videoSectionId] = { type: EXPERIMENT_SPLIT };
			clonedSectionLevelSplit.splits[videoSectionId].controlExperimentSplits = {
				[selectedExistingUnitSectionRef]: { splitPercentage: 0 },
				[counterpartSectionRef]: { splitPercentage: 0 }
			};
		}
		if (selectedSectionSplitType === NORMAL_SPLIT) {
			clonedSectionLevelSplit.splits[videoSectionId] = {
				type: NORMAL_SPLIT,
				splitPercentage: 0,
				splitDescription
			};
		}
		return clonedSectionLevelSplit;
	};

	const addNewSplitSection = () => {
		try {
			checkIfNewSplitCanBeAdded(
				units,
				selectedExistingUnitSectionRef,
				selectedSectionSplitType,
				sectionLevelSplit,
				splitDescription
			); // validation check before adding split
			const newSplitConfig = modifySplitConfigToAddNewSplit();
			updateSectionLevelSplit(newSplitConfig);
			updateModalContent(null);
		} catch (error) {
			toast.error(error.message, {
				position: toast.POSITION.BOTTOM_RIGHT
			});
		}
	};

	/**
	 * @description Returns split config types excluding MULTI_SPLIT.
	 * @returns {Array<{ label: string, value: string }>}
	 */
	const getSplitConfigTypesWithoutMultiSplit = () => {
		return Object.keys(SPLIT_CONFIG_TYPES)
			.filter((key) => SPLIT_CONFIG_TYPES[key] !== SPLIT_CONFIG_TYPES.MULTI_SPLIT)
			.map((key) => ({
				label: SPLIT_CONFIG_TYPES[key],
				value: SPLIT_CONFIG_TYPES[key]
			}));
	};

	return (
		<Grid
			container
			spacing={2}
			className={commonStyleClasses.modalContentStyle}
			style={{ width: 'fit-content', height: 'fit-content' }}
		>
			<Grid item xs={12}>
				<Typography className={commonStyleClasses.heading} fontWeight={600} variant="h6">
					Add New Section Split
				</Typography>
			</Grid>
			<Grid item xs={12} className={commonStyleClasses.modalContentItem}>
				<DropDownSingle
					directValue={selectedExistingUnitSectionRef}
					updateDirectValue={(value) => {
						setSelectedExistingUnitSectionRef(value);
					}}
					isObjectEditor={false}
					formControlVariant="outlined"
					list={availableSections.map((sectionRef) => {
						const sectionName = units[sectionRef].name;
						return {
							label: sectionName,
							value: sectionRef
						};
					})}
				/>
			</Grid>
			<Grid item xs={12}>
				<Typography className={commonStyleClasses.heading} fontWeight={600} variant="h6">
					Section Split Type
				</Typography>
			</Grid>
			<Grid item xs={12} className={commonStyleClasses.modalContentItem}>
				<FormControl>
					<DropDownSingle
						directValue={selectedSectionSplitType}
						selectInputStyles={{ width: '20rem' }}
						updateDirectValue={(value) => {
							setSelectedSectionSplitType(value);
						}}
						isObjectEditor={false}
						formControlVariant="outlined"
						list={getSplitConfigTypesWithoutMultiSplit()}
					/>
				</FormControl>
			</Grid>
			{selectedSectionSplitType === NORMAL_SPLIT && (
				<>
					<Grid item xs={12}>
						<Typography className={commonStyleClasses.heading} fontWeight={600} variant="h6">
							Add Split Description
						</Typography>
					</Grid>

					<InputConfig
						inputType="text"
						keyData={"NORMAL_SPLIT_DESCRIPTION"}
						inputStyles={{ width: '100%', margin: '1rem 0' }}
						labelStyles={{ fontSize: '1.7rem' }}
						isObjectEditor={false}
						directValue={splitDescription ?? "Please enter a description"}
						updateDirectValue={(value) => {
							setSplitDescription(value);
						}}
					/>
				</>
			)}

			<Grid item xs={12} className={commonStyleClasses.modalContentItem}>
				<Button
					variant="contained"
					style={{ width: '10rem', height: '5rem' }}
					onClick={addNewSplitSection}
				>
					ADD
				</Button>
			</Grid>
		</Grid>
	);
});

export default VideoSectionsSplitConfig;
